import { Inject, Injectable } from '@angular/core';
import { Inspection } from './inspection.dto';
import { HttpUtils, ID_FIELD_OPTIONS, Query, SearchResult, WW_INSPECTIONS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { COMMONS_ENV, HttpService } from '@damofire/data-angular-commons';
import { deleteDoc, doc, docData, DocumentData, getFirestore } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { InspectionService } from './inspection.service';
import { FirebaseObservableInspectionService } from './firebase-inspection-observable.service';
import { FirebaseAssetService } from '../asset/firebase-asset.service';
import { FirebaseIssueService } from '../issue/firebase-issue.service';
import { logger } from '@sentry/angular';

@Injectable()
export class FirebaseInspectionService extends InspectionService {
  constructor(
    private readonly dtoSigner: AuthDtoSignerService,
    protected readonly http: HttpService,
    protected readonly assetService: FirebaseAssetService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    protected readonly issueService: FirebaseIssueService,
    protected readonly observableInspectionService: FirebaseObservableInspectionService,
  ) {
    super(http, environment, issueService, assetService);
  }

  async dbCreate(inspection: Partial<Inspection>): Promise<Inspection> {
    await this.dtoSigner.set(inspection, `${WW_INSPECTIONS_COLLECTION}/${inspection._id}`);
    return inspection;
  }

  async update(division: string, id: string, update: DocumentData | Inspection): Promise<void> {
    logger.info('Updating inspection (firebase)', { category: 'inspection', id, division });
    await this.dtoSigner.update(update, `${WW_INSPECTIONS_COLLECTION}/${id}`);
  }

  async delete(division: string, id: string): Promise<void> {
    return deleteDoc(doc(getFirestore(), `${WW_INSPECTIONS_COLLECTION}/${id}`));
  }

  async deleteBatch(division: string, inspectionIds: string[], deleteIssues = false): Promise<void> {
    logger.info('Deleting inspection batch (firebase)', { category: 'inspection', division, count: inspectionIds.length, deleteIssues });
    await HttpUtils.post(this.environment.appConfig.fns, 'ww-inspectionsDeleteAll', {
      data: {
        inspectionIds,
        divisionId: division,
        deleteIssues,
      },
    });
  }

  async search(division: string, sQuery: Query): Promise<SearchResult<Inspection>> {
    return firstValueFrom(this.observableInspectionService.search(division, sQuery));
  }

  async get(division: string, id: string): Promise<Inspection> {
    return firstValueFrom(docData<Inspection>(doc(getFirestore(), `${WW_INSPECTIONS_COLLECTION}/${id}`), ID_FIELD_OPTIONS));
  }
}
